/**
 * TikTok-style single clip: forwardRef with play()/stop().
 * Parent feed calls play() on viewable item and stop() on others so only one video plays.
 * Uses same video + overlay as before; ensures video source loads (preload, resolved URL).
 */
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {Image, Platform, Pressable, StyleSheet, View} from 'react-native'
import {LinearGradient} from 'expo-linear-gradient'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {atoms as a, useTheme} from '#/alf'
import {STUDIO_API_BASE} from '#/lib/api/studio'
import {formatViewCount} from '#/lib/strings/view-count'
import {Text} from '#/components/Typography'
import {Eye_Stroke2_Corner0_Rounded as Eye} from '#/components/icons/Eye'
import {Message_Stroke2_Corner0_Rounded as Message} from '#/components/icons/Message'
import {StudioClipVideoWeb} from '#/screens/Studio/StudioClipVideoWeb'
import type {StudioVideo} from '#/lib/api/studio'

function resolveStudioMediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const base = STUDIO_API_BASE.replace(/\/$/, '')
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`
}

function ClipNativeVideo({
  videoPath,
  posterUri,
  paused,
  onTap,
}: {
  videoPath: string
  posterUri: string | null
  paused: boolean
  onTap: () => void
}) {
  const t = useTheme()
  const expoVideo = useMemo(() => {
    try {
      return require('expo-video')
    } catch {
      return null
    }
  }, [])
  const player = useMemo(() => {
    if (!expoVideo) return null
    return expoVideo.createVideoPlayer(videoPath, (p: {loop: boolean; muted: boolean}) => {
      p.loop = true
      p.muted = false
    })
  }, [expoVideo, videoPath])

  useEffect(() => {
    if (!player || typeof player.play !== 'function' || typeof player.pause !== 'function') return
    if (paused) {
      player.pause()
    } else {
      player.play()
    }
  }, [player, paused])

  if (expoVideo && player) {
    const {VideoView} = expoVideo
    return (
      <Pressable
        onPress={onTap}
        style={[a.flex_1, a.w_full, {backgroundColor: t.palette.black}]}
        accessibilityRole="button"
        accessibilityLabel="Tap to pause or play">
        <VideoView
          player={player}
          style={[a.flex_1, a.w_full]}
          nativeControls={false}
          contentFit="cover"
        />
      </Pressable>
    )
  }
  return (
    <View style={[a.flex_1, a.justify_center, a.align_center, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
        <Trans>Clip</Trans>
      </Text>
    </View>
  )
}

export interface ClipSingleHandles {
  play: () => void
  stop: () => void
}

export const ClipSingle = forwardRef<
  ClipSingleHandles,
  {
    item: StudioVideo
    onOpenComments?: (videoId: string, videoTitle: string) => void
    onPlayStateChange?: (playing: boolean, paused: boolean) => void
  }
>(({item: video, onOpenComments, onPlayStateChange}, ref) => {
    const {_} = useLingui()
    const t = useTheme()
    const [playing, setPlaying] = useState(false)
    const [paused, setPaused] = useState(false)

    useEffect(() => {
      onPlayStateChange?.(playing, paused)
    }, [playing, paused, onPlayStateChange])

    useImperativeHandle(ref, () => ({
      play: () => {
        setPlaying(true)
        setPaused(false)
      },
      stop: () => {
        setPlaying(false)
      },
    }), [])

    const onTogglePause = useCallback(() => setPaused(p => !p), [])
    const onTap = useCallback(() => {
      if (playing) {
        onTogglePause()
      } else {
        setPlaying(true)
        setPaused(false)
      }
    }, [playing, onTogglePause])
    const onCommentPress = useCallback(() => {
      if (onOpenComments) onOpenComments(video.id, video.title || _(msg`Untitled`))
    }, [onOpenComments, video.id, video.title, _])

    const videoSrc = resolveStudioMediaUrl(video.video_path) ?? video.video_path
    const posterUri = resolveStudioMediaUrl(video.thumbnail_path) ?? video.thumbnail_path ?? undefined
    const canPlayInline = Platform.OS === 'web'
    const showVideo = playing

    return (
      <Pressable
        onPress={onTap}
        style={[a.flex_1, a.rounded_none, a.overflow_hidden]}
        accessibilityRole="button"
        accessibilityLabel={_(msg`Play clip: ${video.title}`)}>
        <View style={[a.flex_1, {minHeight: 400, backgroundColor: t.palette.black}]}>
          {(posterUri || video.thumbnail_path) && (
            <Image
              source={{uri: posterUri || video.thumbnail_path!}}
              style={[StyleSheet.absoluteFillObject]}
              resizeMode="cover"
            />
          )}
          {showVideo && canPlayInline && (
            <StudioClipVideoWeb
              src={videoSrc || video.video_path}
              poster={posterUri}
              isActive={true}
              paused={paused}
              onTap={onTogglePause}
              videoId={video.id}
            />
          )}
          {showVideo && !canPlayInline && (
            <ClipNativeVideo
              videoPath={videoSrc || video.video_path}
              posterUri={posterUri || video.thumbnail_path}
              paused={paused}
              onTap={onTogglePause}
            />
          )}
          <View
            style={[a.absolute, a.inset_0, a.flex_row, a.justify_between]}
            pointerEvents="box-none">
            <View style={[a.justify_end, a.p_md, {flex: 1}]} pointerEvents="none">
              <LinearGradient
                colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0)']}
                locations={[0, 0]}
                style={[a.absolute, a.inset_0]}
              />
              <View style={[a.relative, a.pt_xl]}>
                <Text style={[a.text_lg, a.font_semi_bold, {color: 'white'}]} numberOfLines={2}>
                  {video.title || _(msg`Untitled`)}
                </Text>
                {video.description ? (
                  <Text style={[a.text_sm, a.pt_xs, {color: 'rgba(255,255,255,0.9)'}]} numberOfLines={1}>
                    {video.description}
                  </Text>
                ) : null}
              </View>
            </View>
            <View
              style={[
                a.justify_end,
                a.p_md,
                a.gap_lg,
                a.align_center,
                {backgroundColor: 'transparent'},
              ]}
              pointerEvents="box-none"
            >
              <View style={[a.align_center]} pointerEvents="none">
                <View style={[a.rounded_full, a.align_center, a.justify_center, {width: 48, height: 48, backgroundColor: 'transparent'}]}>
                  <Eye size="lg" fill="white" />
                </View>
                <Text
                  style={[
                    a.text_xs,
                    {color: 'white', textShadowColor: 'rgba(0,0,0,0.8)', textShadowOffset: {width: 0, height: 1}, textShadowRadius: 2},
                    a.pt_xs,
                  ]}>
                  {formatViewCount(video.total_views)}
                </Text>
              </View>
              {onOpenComments && (
                <View style={[a.align_center]} pointerEvents="box-none">
                  <Pressable
                    onPress={e => {
                      e?.stopPropagation?.()
                      onCommentPress()
                    }}
                    style={[a.rounded_full, a.align_center, a.justify_center, {width: 48, height: 48, backgroundColor: 'transparent'}]}
                    accessibilityRole="button"
                    accessibilityLabel={_(msg`Comments`)}>
                    <Message size="lg" fill="white" />
                  </Pressable>
                  <Text
                    style={[
                      a.text_xs,
                      {color: 'white', textShadowColor: 'rgba(0,0,0,0.8)', textShadowOffset: {width: 0, height: 1}, textShadowRadius: 2},
                      a.pt_xs,
                    ]}>
                    <Trans>Comments</Trans>
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </Pressable>
    )
  },
)
